import { useState } from 'react'
import { useCreateAudience } from '@/api/hooks/use-audiences'
import { Modal } from '@/components/ui/modal'
import { UnsubFeedbackSection } from '@/pages/audiences/unsub-feedback-section'

interface AudienceCreateModalProps {
  open: boolean
  onClose: () => void
  onCreated: (id: string) => void
}

export function AudienceCreateModal({
  open,
  onClose,
  onCreated,
}: AudienceCreateModalProps): React.JSX.Element {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [unsubMessage, setUnsubMessage] = useState('')
  const [unsubReasons, setUnsubReasons] = useState<string[]>([])
  const [includeOther, setIncludeOther] = useState(false)
  const createAudience = useCreateAudience()

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (!name.trim()) return

    const reasons = unsubReasons.filter((r) => r.trim())
    if (includeOther) reasons.push('Other')

    createAudience.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        unsub_message: unsubMessage.trim() || null,
        unsub_reasons: reasons.length > 0 ? reasons : null,
      },
      {
        onSuccess: (result) => {
          setName('')
          setDescription('')
          setUnsubMessage('')
          setUnsubReasons([])
          setIncludeOther(false)
          onClose()
          onCreated(result.id)
        },
      },
    )
  }

  return (
    <Modal open={open} onClose={onClose} title="Create Audience" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="audience-name" className="block text-sm font-medium text-text-primary">
            Name
          </label>
          <input
            id="audience-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., All Subscribers"
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="audience-desc" className="block text-sm font-medium text-text-primary">
            Description (optional)
          </label>
          <textarea
            id="audience-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A brief description of this audience"
            rows={2}
            className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
          />
        </div>

        <UnsubFeedbackSection
          unsubMessage={unsubMessage}
          onUnsubMessageChange={setUnsubMessage}
          reasons={unsubReasons}
          onReasonsChange={setUnsubReasons}
          includeOther={includeOther}
          onIncludeOtherChange={setIncludeOther}
          audienceName={name || undefined}
        />

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || createAudience.isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {createAudience.isPending ? 'Creating...' : 'Create Audience'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
